import type { ReactElement } from "react";

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, ChevronLeft, ChevronRight, List, AlertTriangle } from "lucide-react";
import PageLayout from "../components/PageLayout";

/**
 * Policy section
 */
interface PolicySection {
  id: string;
  title: string;
  shortTitle: string;
  content: string;
}

const LAST_UPDATED = "January 20, 2026";

const SECTIONS: PolicySection[] = [
  {
    id: "introduction",
    title: "1. Introduction",
    shortTitle: "Introduction",
    content: `Mytho Labs ("Company," "we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI-powered storybook creation service ("Service").

By using our Service, you consent to the data practices described in this policy. If you do not agree with the terms of this Privacy Policy, please do not access the Service.`,
  },
  {
    id: "information-collected",
    title: "2. Information We Collect",
    shortTitle: "Data Collected",
    content: `We collect information in the following ways:

Information You Provide:
• Account information: name, email address, password (or Google account authentication)
• Profile information: display name, profile picture
• Payment information: processed securely through Stripe (we do not store credit card numbers)
• Story prompts and preferences: the text you enter to create stories
• Support inquiries: messages sent through our contact form

Information Collected Automatically:
• Usage data: pages visited, features used, time spent on the Service
• Device information: browser type, operating system, device identifiers
• Log data: IP address, access times, referring URLs
• Cookies and similar technologies: for authentication and analytics

Photo Transform Feature (Premium):
If you use our Photo Transform feature, we temporarily process uploaded photos to generate illustrated character portraits. Original photos are deleted after processing.`,
  },
  {
    id: "how-we-use",
    title: "3. How We Use Your Information",
    shortTitle: "Data Usage",
    content: `We use the information we collect to:

Provide and Improve the Service:
• Generate personalized stories based on your prompts
• Create AI-generated illustrations
• Provide audio narration features
• Store and display your created stories
• Maintain and improve our AI systems

Account Management:
• Create and manage your account
• Process payments and subscriptions
• Send transactional emails (receipts, account notifications)
• Provide customer support

Safety and Security:
• Detect and prevent fraud or abuse
• Enforce our Terms of Service
• Comply with legal obligations

With Your Consent:
• Send marketing communications (you can opt out at any time)
• Use your stories for promotional purposes (only with explicit consent)`,
  },
  {
    id: "ai-processing",
    title: "4. AI Processing and Training",
    shortTitle: "AI Processing",
    content: `Our Service uses artificial intelligence to generate stories and illustrations. You should understand:

Story Generation:
• Your prompts are processed by AI models to generate story content
• Prompts may be retained for quality assurance and safety monitoring
• We do not use your personal stories to train AI models without consent

Content Safety:
• We employ content filters to prevent generation of harmful content
• Prompts may be reviewed by automated systems for policy compliance
• Flagged content may be reviewed by human moderators

Improvement of Services:
• Anonymized and aggregated usage data may be used to improve our AI systems
• We will not use personally identifiable content from your stories for AI training without explicit consent`,
  },
  {
    id: "data-storage",
    title: "5. Data Storage and Security",
    shortTitle: "Security",
    content: `We implement industry-standard security measures to protect your data:

Security Measures:
• Encryption in transit (HTTPS/TLS)
• Secure data storage with access controls
• Regular security assessments
• Employee access limited on a need-to-know basis

Data Retention:
• Account data: retained while your account is active
• Stories: retained until you delete them or your account
• Support tickets: retained for 3 years for service improvement
• Usage logs: retained for 90 days for security purposes

Data Location:
• Our services are hosted in the United States
• By using our Service, you consent to data processing in the U.S.`,
  },
  {
    id: "sharing",
    title: "6. Sharing Your Information",
    shortTitle: "Sharing",
    content: `We do not sell your personal information. We may share data in these circumstances:

Service Providers:
• Payment processing: Stripe
• Email delivery: SendGrid
• Cloud hosting: Our infrastructure providers
• Analytics: Aggregated, anonymized data only

Legal Requirements:
• To comply with laws or legal processes
• To protect our rights and safety
• To investigate potential violations

Business Transfers:
• In connection with a merger, acquisition, or sale of assets
• You would be notified of any change in data practices

With Your Consent:
• When you explicitly authorize sharing
• When sharing public stories with the community`,
  },
  {
    id: "coppa",
    title: "7. Children's Privacy (COPPA Compliance)",
    shortTitle: "Children",
    content: `We take children's privacy seriously and comply with the Children's Online Privacy Protection Act (COPPA).

Age Requirements:
• Users must be at least 13 years old to create an account
• Users under 18 require parental or guardian consent
• Parents/guardians should supervise children's use of the Service

Parental Controls:
• Parents can review their child's account and content
• Parents can request deletion of their child's data
• Parents can disable or delete their child's account

Information from Children:
• We do not knowingly collect personal information from children under 13
• If we discover we have collected data from a child under 13 without parental consent, we will delete it promptly
• Parents who believe their child has provided personal information should contact us immediately

Content Safety for Children:
• Our content filters are designed to prevent inappropriate content
• Stories are reviewed by automated safety systems
• Parents should still supervise children's story creation and viewing`,
  },
  {
    id: "your-rights",
    title: "8. Your Rights and Choices",
    shortTitle: "Your Rights",
    content: `You have the following rights regarding your data:

Access and Portability:
• Request a copy of your personal data
• Export your created stories

Correction and Deletion:
• Update your account information through your Profile
• Request deletion of your account and associated data
• Delete individual stories from your Library

Communication Preferences:
• Opt out of marketing emails at any time
• Manage notification preferences in your Profile

Do Not Track:
• We currently do not respond to "Do Not Track" browser signals

California Residents (CCPA):
• Right to know what personal information is collected
• Right to delete personal information
• Right to opt out of the sale of personal information (we do not sell data)
• Right to non-discrimination for exercising privacy rights

European Residents (GDPR):
If you are in the European Economic Area, you also have rights to:
• Object to processing of your data
• Restrict processing of your data
• Data portability
• Lodge a complaint with a supervisory authority`,
  },
  {
    id: "cookies",
    title: "9. Cookies and Tracking Technologies",
    shortTitle: "Cookies",
    content: `We use cookies and similar technologies for:

Essential Cookies:
• Authentication and session management
• Security features
• These cannot be disabled

Functional Cookies:
• Remembering your preferences
• Improving your experience

Analytics Cookies:
• Understanding how the Service is used
• Improving features and performance
• These can be disabled in your browser settings

Managing Cookies:
• Most browsers allow you to control cookies through settings
• Disabling cookies may affect functionality
• We do not use cookies for advertising`,
  },
  {
    id: "third-party",
    title: "10. Third-Party Links",
    shortTitle: "Third-Party",
    content: `Our Service may contain links to third-party websites or services. We are not responsible for the privacy practices of these third parties.

We encourage you to review the privacy policies of any third-party sites you visit. Our Privacy Policy only applies to information collected through our Service.`,
  },
  {
    id: "changes",
    title: "11. Changes to This Policy",
    shortTitle: "Changes",
    content: `We may update this Privacy Policy from time to time. We will notify you of material changes by:

• Posting the updated policy on our website
• Updating the "Last Updated" date
• Sending an email to your registered address (for material changes)

Your continued use of the Service after changes become effective constitutes acceptance of the updated policy.`,
  },
  {
    id: "contact",
    title: "12. Contact Us",
    shortTitle: "Contact",
    content: `If you have questions about this Privacy Policy or our data practices, please contact us:

Mytho Labs
Email: support@mytho-labs.com
Website: https://tellor.mytho-labs.com/contact

For privacy-specific inquiries, please include "Privacy" in the subject line.

For requests regarding children's data under COPPA, parents should contact us with verification of their parental relationship.`,
  },
];

/**
 * PrivacyPage Component
 *
 * Privacy policy page with table of contents and sections.
 */
const PrivacyPage = (): ReactElement => {
  const navigate = useNavigate();
  const [showToc, setShowToc] = useState<boolean>(false);

  const scrollToSection = (id: string): void => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setShowToc(false);
  };

  return (
    <PageLayout>
      <div className="min-h-screen pb-16" style={{ background: "var(--st-bg-page)" }}>
        {/* Back Button */}
        <div className="max-w-3xl mx-auto px-4 pt-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-sm mb-4 transition-all hover:opacity-70"
            style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>
        </div>

        {/* Page Header Card */}
        <div className="max-w-3xl mx-auto px-4">
          <div
            className="rounded-2xl p-6 mb-6"
            style={{
              background: "var(--st-bg-surface)",
              border: "1px solid var(--st-border-light)",
              boxShadow: "0 4px 12px rgba(92, 64, 51, 0.06)",
            }}
          >
            <div className="flex flex-col items-center text-center">
              <div
                className="w-16 h-16 mb-4 rounded-2xl flex items-center justify-center"
                style={{
                  background:
                    "linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)",
                }}
              >
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h1
                className="text-2xl md:text-3xl font-bold mb-2"
                style={{ fontFamily: "Chewy, cursive", color: "var(--st-text-heading)" }}
              >
                Privacy Policy
              </h1>
              <p
                className="text-sm"
                style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
              >
                Last updated: {LAST_UPDATED}
              </p>

              {/* Table of Contents Toggle - Mobile */}
              <button
                onClick={() => setShowToc(!showToc)}
                className="mt-4 flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all hover:scale-[1.02] md:hidden"
                style={{
                  background: "var(--st-bg-page)",
                  border: "1px solid var(--st-border-light)",
                  fontFamily: "Quicksand, sans-serif",
                  color: "var(--st-text-heading)",
                }}
              >
                <List className="w-4 h-4" />
                {showToc ? "Hide" : "Show"} Table of Contents
              </button>
            </div>
          </div>
        </div>

        {/* COPPA Notice Banner */}
        <div className="max-w-3xl mx-auto px-4 mb-6">
          <div
            className="flex items-start gap-3 p-4 rounded-xl"
            style={{
              background: "#FEF3C7",
              border: "1px solid #FDE68A",
            }}
          >
            <AlertTriangle
              className="w-5 h-5 flex-shrink-0 mt-0.5"
              style={{ color: "#D97706" }}
            />
            <div>
              <p
                className="text-sm font-semibold mb-1"
                style={{
                  fontFamily: "Quicksand, sans-serif",
                  color: "#92400E",
                }}
              >
                Important Notice for Parents
              </p>
              <p
                className="text-xs"
                style={{
                  fontFamily: "Quicksand, sans-serif",
                  color: "#92400E",
                }}
              >
                Mytho Labs is committed to protecting children's privacy. We
                comply with the Children's Online Privacy Protection Act
                (COPPA). See{" "}
                <button
                  onClick={() => scrollToSection("coppa")}
                  className="underline font-semibold hover:opacity-70"
                >
                  Section 7
                </button>{" "}
                for details on how we handle children's information and your
                parental rights.
              </p>
            </div>
          </div>
        </div>

        {/* Mobile TOC Dropdown */}
        {showToc && (
          <div className="max-w-3xl mx-auto px-4 mb-6 md:hidden">
            <div
              className="rounded-xl p-4"
              style={{ background: "var(--st-bg-surface)", border: "1px solid var(--st-border-light)" }}
            >
              <div className="space-y-1">
                {SECTIONS.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className="w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between transition-all hover:bg-orange-50 dark:hover:bg-orange-950/30"
                    style={{
                      fontFamily: "Quicksand, sans-serif",
                      color: "var(--st-text-heading)",
                    }}
                  >
                    {section.shortTitle}
                    <ChevronRight className="w-4 h-4" style={{ color: "var(--st-text-muted)" }} />
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex gap-6">
            {/* Desktop Table of Contents - Sticky Sidebar */}
            <div className="hidden md:block w-48 flex-shrink-0">
              <div
                className="sticky top-24 rounded-xl p-4"
                style={{ background: "var(--st-bg-surface)", border: "1px solid var(--st-border-light)" }}
              >
                <p
                  className="text-xs font-semibold mb-3 uppercase tracking-wide"
                  style={{
                    fontFamily: "Quicksand, sans-serif",
                    color: "var(--st-text-muted)",
                  }}
                >
                  Contents
                </p>
                <div className="space-y-1">
                  {SECTIONS.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className="w-full text-left px-2 py-1.5 rounded-lg text-xs transition-all hover:bg-orange-50 dark:hover:bg-orange-950/30"
                      style={{
                        fontFamily: "Quicksand, sans-serif",
                        color: "var(--st-text-heading)",
                      }}
                    >
                      {section.shortTitle}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1">
              <div
                className="rounded-xl p-6 md:p-8"
                style={{
                  background: "var(--st-bg-surface)",
                  border: "1px solid var(--st-border-light)",
                  boxShadow: "0 4px 12px rgba(92, 64, 51, 0.06)",
                }}
              >
                {/* Sections */}
                <div className="space-y-8">
                  {SECTIONS.map((section) => (
                    <div key={section.id} id={section.id} className="scroll-mt-24">
                      <h2
                        className="text-lg font-bold mb-3"
                        style={{ fontFamily: "Chewy, cursive", color: "var(--st-text-heading)" }}
                      >
                        {section.title}
                      </h2>
                      <div
                        className="text-sm leading-relaxed whitespace-pre-wrap"
                        style={{
                          fontFamily: "Quicksand, sans-serif",
                          color: "var(--st-text-heading)",
                        }}
                      >
                        {section.content}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Links */}
        <div className="max-w-3xl mx-auto px-4 mt-8">
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={() => navigate("/terms")}
              className="text-xs px-3 py-1.5 rounded-lg transition-all hover:bg-white/80 dark:hover:bg-white/10"
              style={{
                fontFamily: "Quicksand, sans-serif",
                color: "var(--st-text-muted)",
              }}
            >
              Terms of Service
            </button>
            <span style={{ color: "var(--st-border-light)" }}>|</span>
            <button
              onClick={() => navigate("/help")}
              className="text-xs px-3 py-1.5 rounded-lg transition-all hover:bg-white/80 dark:hover:bg-white/10"
              style={{
                fontFamily: "Quicksand, sans-serif",
                color: "var(--st-text-muted)",
              }}
            >
              Help & FAQ
            </button>
            <span style={{ color: "var(--st-border-light)" }}>|</span>
            <button
              onClick={() => navigate("/contact")}
              className="text-xs px-3 py-1.5 rounded-lg transition-all hover:bg-white/80 dark:hover:bg-white/10"
              style={{
                fontFamily: "Quicksand, sans-serif",
                color: "var(--st-text-muted)",
              }}
            >
              Contact Us
            </button>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default PrivacyPage;
